import { FC } from 'react'

import { useParams, type Params } from 'react-router'

import type { Network } from '@bettermode/common/gql/generated'
import { legacyMatchPath } from '@bettermode/common/legacy-match-path'
import { isForbidden, isNotFound } from '@tribeplatform/gql-client/lib'
import { useI18n } from '@tribeplatform/react-components/i18n'
import { SlateLayout } from '@tribeplatform/react-components/LayoutFrame'
import { useRouterPath } from '@tribeplatform/react-sdk'
import { generatePath } from '@tribeplatform/react-sdk/utils'
import type { SlateRendererProps } from '@tribeplatform/react-slate-kit/components'
import { Container } from '@tribeplatform/react-ui-kit/Layout'

import { useCurrentPage } from '../hooks/useCurrentPage.js'

import { NotFound } from './Error/NotFound.js'
import { PageLayout } from './Layout/PageLayout.js'
import { PageMeta } from './Meta/PageMeta.js'
import { ContentModuleModalRenderer } from './Space/ContentModuleModalRenderer.js'
import { Space } from './Space/Space.js'
import { GenericSpaceLoading } from './Space/SpaceLoading.js'

const getSpaceSlateContext = (
  spacePath: string,
  exact: boolean,
  pathname: string,
  urlParams: Readonly<Params<string>>,
): SlateRendererProps['context'] => {
  if (spacePath) {
    const { params } =
      legacyMatchPath(pathname, {
        path: `/${spacePath ?? ''}`,
        exact,
      }) ?? {}

    return {
      path: generatePath(spacePath, params),
      urlParams: params || {},
    }
  }

  return {
    path: pathname,
    urlParams,
  }
}

export const SpaceRoutes: FC<{
  networkName: Network['name']
  headerDisabled: Network['navigationSlates']['headerDisabled']
}> = ({ networkName, headerDisabled }) => {
  const { $t } = useI18n()
  const urlParams = useParams()
  const pathname = useRouterPath()
  const {
    data: page,
    error,
    isInitialLoading,
  } = useCurrentPage({
    useQueryOptions: {
      retry: 0,
    },
  })
  const { space } = page || {}
  const context = getSpaceSlateContext(
    space?.address?.path,
    space?.address?.exact,
    pathname,
    urlParams,
  )

  if (isInitialLoading && !space) {
    return (
      <PageLayout
        forceLayout={
          headerDisabled
            ? SlateLayout.noHeaderNoSidebar
            : SlateLayout.headerNoSidebar
        }
      >
        <Container
          size="full"
          padding={{
            vertical: 'md',
            horizontal: { size: 'md', disableOnMobile: true },
          }}
        >
          <GenericSpaceLoading />
        </Container>
      </PageLayout>
    )
  }

  if (!space || !space.slug || isNotFound(error) || isForbidden(error)) {
    return (
      <PageLayout>
        <Container
          size="full"
          padding={{
            vertical: 'md',
            horizontal: { size: 'md', disableOnMobile: true },
          }}
        >
          <NotFound
            title={$t({
              defaultMessage: 'Page not found',
              id: 'Page.NotFound',
            })}
            description={$t({
              defaultMessage:
                "This page does not exist, is removed, or you don't have permission to access it.",
              id: 'Page.NotFound.Description',
            })}
          />
        </Container>
      </PageLayout>
    )
  }

  return (
    <>
      {page && (
        <PageMeta
          seoTitle={page?.seoDetail?.title}
          seoDescription={page?.seoDetail?.description}
          seoAdditionalMeta={page?.seoDetail?.additionalMeta}
          seoJsonld={page?.seoDetail?.jsonld}
          pageSpaceAddressPath={page?.space?.address?.path}
        />
      )}
      <Space
        networkName={networkName}
        seoTitle={page?.seoDetail?.title}
        space={space}
        context={context}
      />
      <ContentModuleModalRenderer />
    </>
  )
}
